import { appConfigurationQueryOptions } from "@/api/app-configuration.api";
import { creditsQueryOptions } from "@/api/credits.api";
import { Route as RootRoute } from "@/routes/__root";
import { useAuth } from "@/services/auth.service";
import { Feature } from "@/types/auth";
import { api } from "@/utils/api";
import { formatMoney } from "@/utils/money";
import { formatNumber } from "@/utils/number-utils";
import {
  getCurrentContract,
  getLicenseData,
} from "@/utils/organization-helpers";
import { pluralize } from "@/utils/string-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, Info } from "lucide-react";
import { useMemo, useState } from "react";
import { Form, Button as RACButton } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import z from "zod";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";
import { Card } from "../Card";
import { AlertModal } from "../generic/AlertModal";
import { Button } from "../generic/Button";
import { Checkbox } from "../generic/Checkbox";
import { Separator } from "../generic/Separator";
import { Slider } from "../generic/Slider";
import { SettingsContainer } from "../layout/SettingsContainer";

const formSchema = z
  .object({
    view: z.literal(["selectCredits", "confirmPurchase"]),
    creditType: z.literal(["upgrade", "prepaid"]),
    extraCredits: z.number(),
    prepaidCredits: z.number(),
    acceptTerms: z.boolean(),
  })
  .refine(
    (data) => (data.view === "confirmPurchase" ? data.acceptTerms : true),
    {
      message: "Du skal acceptere betingelserne for at fortsætte",
    },
  );

type Props = {
  className?: string;
  reason?: "notEnoughCredits" | "riskOfRunningOut";
  // Custom credit estimates for cases like starting/updating a campaign, where we know what they will be after the change, but they are not yet reflected in the credits API.
  creditsNeededRestOfPeriod?: number;
  creditsNeededNextPeriod?: number;
  creditEstimateRestOfPeriod?: number;
  creditEstimateNextPeriod?: number;
  neededCredits?: number;
  neededPrepaidCredits?: number;
  allowPrepaidCredits?: boolean;

  onClose: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
};

export function BuyCreditsModal({
  className,
  reason = "notEnoughCredits",
  // creditsNeededRestOfPeriod,
  // creditsNeededNextPeriod,
  creditEstimateRestOfPeriod,
  creditEstimateNextPeriod,
  neededCredits = 0,
  neededPrepaidCredits = neededCredits,
  allowPrepaidCredits = false,

  onClose,
  onSuccess,
  onSkip,
}: Props) {
  const context = RootRoute.useRouteContext();

  const { data: appConfiguration } = useQuery({
    ...appConfigurationQueryOptions,
    initialData: context.appConfiguration,
  });

  const auth = useAuth();

  const contract = useMemo(
    () => getCurrentContract(auth.user.organization),
    [auth.user.organization],
  );

  const currentPlan = useMemo(() => {
    let id = "freemium";
    let name = "Freemium";
    if (auth.featuresSet.has(Feature.LassoSalesPro)) {
      id = "pro";
      name = "Pro";
    } else if (auth.featuresSet.has(Feature.LassoSalesEssentials)) {
      id = "essentials";
      name = "Essentials";
    }

    const versions: Partial<
      Record<
        string,
        (typeof appConfiguration.versions)[keyof typeof appConfiguration.versions]
      >
    > = appConfiguration.versions;
    const price = contract?.price ?? versions[id]?.price ?? 0;

    return { id, name, price };
  }, [appConfiguration, auth.featuresSet, contract]);

  const { currentLicenses, licensePrice } = useMemo(
    () => getLicenseData(auth.user.organization),
    [auth.user.organization],
  );

  const { data: creditsData = appConfiguration, refetch: refetchCredits } =
    useQuery(creditsQueryOptions);

  creditEstimateRestOfPeriod = useMemo(
    () => creditEstimateRestOfPeriod ?? creditsData.creditEstimateRestOfPeriod,
    [creditEstimateRestOfPeriod, creditsData.creditEstimateRestOfPeriod],
  );
  creditEstimateNextPeriod = useMemo(
    () => creditEstimateNextPeriod ?? creditsData.creditEstimateNextPeriod,
    [creditEstimateNextPeriod, creditsData.creditEstimateNextPeriod],
  );

  const upgradeOptions = useMemo(
    () =>
      Object.entries(appConfiguration.creditUpgradeOptions).map(
        ([key, price]) => {
          const credits = Number(key);
          return {
            credits,
            creditsDelta: credits - (creditsData.extraCreditsPerMonth ?? 0),
            price,
          };
        },
      ),
    [appConfiguration.creditUpgradeOptions, creditsData.extraCreditsPerMonth],
  );

  const minValidUpgradeOptionIndex = useMemo(() => {
    const index = upgradeOptions.findIndex(
      (option) => option.creditsDelta >= neededCredits,
    );
    return index === -1 ? upgradeOptions.length : index;
  }, [neededCredits, upgradeOptions]);

  const prepaidCreditOptions = useMemo(
    () =>
      appConfiguration.creditBufferOptions.map((option) => ({
        credits: option.credits,
        price: option.price,
      })),
    [appConfiguration.creditBufferOptions],
  );

  const minValidPrepaidCreditOption = useMemo(
    () =>
      prepaidCreditOptions.find(
        (option) => option.credits >= neededPrepaidCredits,
      ) ||
      prepaidCreditOptions.at(-1) ||
      null,
    [neededPrepaidCredits, prepaidCreditOptions],
  );

  const canUpgrade = useMemo(
    () =>
      !!upgradeOptions.length && appConfiguration.features.canUpgradeCreditTier,
    [upgradeOptions.length],
  );

  // Allow prepaid credits if explicitly allowed or if upgrade is not possible
  allowPrepaidCredits = useMemo(
    () => allowPrepaidCredits || !canUpgrade,
    [allowPrepaidCredits, canUpgrade],
  );

  const { control, handleSubmit, watch, setValue, formState } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      view: "selectCredits",
      creditType: useMemo(
        () => (canUpgrade ? "upgrade" : "prepaid"),
        [canUpgrade],
      ),
      extraCredits: upgradeOptions.at(minValidUpgradeOptionIndex)?.credits ?? 0,
      prepaidCredits: minValidPrepaidCreditOption?.credits ?? 0,
      acceptTerms: false,
    } satisfies z.infer<typeof formSchema>,
  });

  const formValues = watch();

  const selectedUpgradeOptionIndex = useMemo(
    () =>
      upgradeOptions.findIndex(
        (option) => option.credits === formValues.extraCredits,
      ),
    [formValues.extraCredits, upgradeOptions],
  );

  const selectedUpgradeOption = useMemo(
    () =>
      upgradeOptions.at(selectedUpgradeOptionIndex) ?? {
        credits: 0,
        creditsDelta: 0,
        price: 0,
      },
    [selectedUpgradeOptionIndex, upgradeOptions],
  );

  const currentPricePerCredit = useMemo(
    () =>
      creditsData.extraCreditsPerMonth
        ? creditsData.extraCreditsPerMonthPrice /
          creditsData.extraCreditsPerMonth
        : 0,
    [creditsData],
  );
  const selectedPricePerCredit = useMemo(
    () => selectedUpgradeOption.price / selectedUpgradeOption.credits,
    [selectedUpgradeOption],
  );

  const monthlySummary = useMemo(() => {
    const items: {
      name: string;
      price: number;
      removed?: boolean;
    }[] = [];

    items.push({
      name: `Lasso Sales ${currentPlan.name} inkl. ${formatNumber(creditsData.baseCreditsPerMonth)} credits`,
      price: currentPlan.price,
    });

    if (currentLicenses > 1) {
      items.push({
        name: `${formatNumber(currentLicenses)} ${pluralize(currentLicenses, "bruger", "brugere")}`,
        price: licensePrice * (currentLicenses - 1),
      });
    }

    if (creditsData.extraCreditsPerMonth) {
      items.push({
        name: `${formatNumber(creditsData.extraCreditsPerMonth)} ekstra credits`,
        price: creditsData.extraCreditsPerMonthPrice,
        removed: true,
      });
    }

    items.push({
      name: `${formatNumber(selectedUpgradeOption.credits)} ekstra credits`,
      price: selectedUpgradeOption.price,
    });

    return {
      items,
      total: items.reduce(
        (acc, item) => acc + (item.removed ? 0 : item.price),
        0,
      ),
    };
  }, [
    currentPlan.name,
    currentPlan.price,
    creditsData.baseCreditsPerMonth,
    creditsData.extraCreditsPerMonth,
    creditsData.extraCreditsPerMonthPrice,
    currentLicenses,
    licensePrice,
    selectedUpgradeOption.credits,
    selectedUpgradeOption.price,
  ]);

  const selectedPrepaidCreditOption = useMemo(
    () =>
      prepaidCreditOptions.find(
        (option) => option.credits === formValues.prepaidCredits,
      ) ?? { credits: 0, price: 0 },
    [formValues.prepaidCredits, prepaidCreditOptions],
  );

  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const submit = handleSubmit(async (data) => {
    if (data.view === "selectCredits") {
      switch (data.creditType) {
        case "upgrade": {
          if (selectedUpgradeOption.creditsDelta < neededCredits) {
            setWarningModalOpen(true);
            return;
          }
          break;
        }

        case "prepaid": {
          if (selectedPrepaidCreditOption.credits < neededPrepaidCredits) {
            setWarningModalOpen(true);
            return;
          }
          break;
        }
      }

      setValue("view", "confirmPurchase");
    } else if (data.view === "confirmPurchase") {
      try {
        switch (data.creditType) {
          case "upgrade":
            await api.post("apps/sales/settings/credits/buy", {
              json: data.extraCredits,
            });
            break;

          case "prepaid":
            await api.post("apps/sales/settings/credits/buy/buffer", {
              json: data.prepaidCredits,
            });
            break;
        }

        await refetchCredits();

        switch (data.creditType) {
          case "upgrade":
            toast.success("Dit abonnement er opgraderet!");
            break;

          case "prepaid":
            toast.success("Dine credits er føjet til din konto!");
            break;
        }

        await onSuccess();
      } catch {
        toast.error("Der opstod en uventet fejl", {
          description: "Vores teknikere har fået besked og arbejder på sagen.",
        });
      }
    }
  });

  return (
    <SettingsContainer
      className={twMerge("h-auto", className)}
      responsivePadding={false}
      onClickBack={() => {
        if (formState.isSubmitting) return;
        onClose();
      }}
    >
      <AlertModal
        isOpen={warningModalOpen}
        title="For få credits"
        description="Du har valgt færre credits end nødvendigt, for at garantere, at du modtager det ønskede antal leads."
        onConfirm={() => {
          setValue("view", "confirmPurchase");
        }}
        onClose={() => {
          setWarningModalOpen(false);
        }}
        icon={<Info className="text-theme-active" />}
      />

      <Form className="h-full" onSubmit={submit}>
        <AnimatePresence mode="wait">
          {formValues.view === "selectCredits" ? (
            formValues.creditType === "upgrade" ? (
              <motion.div
                key="selectCredits.upgrade"
                className="flex h-full flex-col items-center gap-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <SegmentHeader
                  className="max-w-md"
                  title={
                    reason === "riskOfRunningOut"
                      ? "Undgå at løbe tør for credits"
                      : "Tilkøb flere credits"
                  }
                  description={
                    reason === "riskOfRunningOut"
                      ? `Du har i denne abonnementsperiode ${formatNumber(creditsData.creditsRemaining)} credits tilbage, men dine kampagner vil bruge op til ${formatNumber(creditEstimateRestOfPeriod)} credits. Tilkøb flere credits for at dække dine behov.`
                      : neededCredits
                        ? "Du har ikke nok credits til at dække dine ønskede leads. Tilkøb flere credits til dit månedlige abonnement, for ikke at løbe tør."
                        : "Vælg hvor mange ekstra credits du ønsker at tilkøbe. De tilkøbte credits vil blive lagt oveni det antal, der er inkluderet i dit abonnement."
                  }
                />

                <div className="flex w-full flex-col items-center gap-y-10">
                  <div className="flex w-full justify-center px-16">
                    <Controller
                      control={control}
                      name="extraCredits"
                      render={({ field }) => (
                        <Slider
                          className="max-w-xl"
                          aria-label="Vælg antal credits"
                          minValue={0}
                          maxValue={Math.max(upgradeOptions.length - 1, 0)}
                          step={1}
                          value={[selectedUpgradeOptionIndex]}
                          onChange={([index]) => {
                            const option = upgradeOptions.at(index);
                            if (!option) return;
                            field.onChange(option.credits);
                          }}
                          thumbTooltip={(index) =>
                            `+${formatNumber(upgradeOptions.at(index)?.creditsDelta ?? 0)} credits/md`
                          }
                          minValidValue={minValidUpgradeOptionIndex}
                          progressStyle="theme"
                        />
                      )}
                    />
                  </div>

                  <div className="max-w-lg text-center text-xs/[20px]">
                    {!!neededCredits && (
                      <>
                        Du har brug for mindst +{formatNumber(neededCredits)}{" "}
                        credits/md for at dække dine ønskede leads. <br />
                      </>
                    )}
                    Du vil stige til{" "}
                    {formatNumber(selectedUpgradeOption.credits)} ekstra
                    credits/md til{" "}
                    {currentPricePerCredit !== 0 &&
                      currentPricePerCredit !== selectedPricePerCredit && (
                        <>
                          <span className="text-theme-on-background-variant line-through">
                            {formatNumber(currentPricePerCredit)}
                          </span>{" "}
                        </>
                      )}
                    {formatNumber(selectedPricePerCredit)} kr/credit.
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap-reverse justify-center gap-6 *:grow">
                  {onSkip && (
                    <Button
                      intent="secondary"
                      animate
                      onPress={() => {
                        if (formState.isSubmitting) return;
                        onSkip();
                      }}
                    >
                      Spring over
                    </Button>
                  )}

                  <Button type="submit" animate>
                    Videre
                  </Button>
                </div>

                {allowPrepaidCredits && (
                  <div className="max-w-md text-center text-xs/[20px]">
                    Ønsker du at købe et fast antal af credits, som ikke har
                    påvirkning på dit månedlige abonnement?{" "}
                    <RACButton
                      className="text-theme-active inline cursor-pointer hover:underline"
                      onPress={() => {
                        setValue("creditType", "prepaid");
                      }}
                    >
                      Køb credit klippekort her
                    </RACButton>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="selectCredits.prepaid"
                className="flex h-full flex-col items-center gap-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <SegmentHeader
                  className="max-w-md"
                  title="Køb credit klippekort"
                  description="Køb et fast antal credits som et engangsbeløb. Disse credits påvirker ikke dit månedlige abonnement og udløber ikke."
                />

                <div className="flex w-full flex-col items-center gap-y-10">
                  <div className="flex w-full max-w-md flex-col">
                    <div className="grid grow grid-cols-1 gap-4">
                      {prepaidCreditOptions.map((option) => (
                        <Controller
                          key={option.credits}
                          control={control}
                          name="prepaidCredits"
                          render={({ field }) => (
                            <label className="border-theme-outline-variant hover:border-theme-outline relative flex cursor-pointer items-center gap-x-4 rounded-lg border p-8 transition-colors">
                              <input
                                {...field}
                                className="sr-only"
                                type="radio"
                                value={option.credits}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              />

                              <div className="border-theme-outline-variant flex size-8 items-center justify-center rounded border">
                                {formValues.prepaidCredits ===
                                  option.credits && (
                                  <CheckIcon
                                    size={20}
                                    className="text-green-600 dark:text-green-400"
                                  />
                                )}
                              </div>

                              <div className="flex grow items-center justify-between text-base font-medium">
                                {formatNumber(option.credits)} credits
                              </div>

                              <div className="flex flex-col text-right">
                                <span className="text-base font-semibold">
                                  {formatMoney(option.price)}
                                </span>
                              </div>
                            </label>
                          )}
                        />
                      ))}
                    </div>
                  </div>

                  {!!neededPrepaidCredits && (
                    <div className="max-w-lg text-center text-xs/[20px]">
                      Du har brug for mindst{" "}
                      {formatNumber(neededPrepaidCredits)} credits for at dække
                      dine ønskede leads.
                    </div>
                  )}
                </div>

                <div className="mt-auto flex flex-wrap-reverse justify-center gap-6 *:grow">
                  {canUpgrade ? (
                    <Button
                      intent="secondary"
                      animate
                      onPress={() => {
                        setValue("creditType", "upgrade");
                      }}
                    >
                      Tilbage
                    </Button>
                  ) : (
                    onSkip && (
                      <Button
                        intent="secondary"
                        animate
                        onPress={() => {
                          if (formState.isSubmitting) return;
                          onSkip();
                        }}
                      >
                        Spring over
                      </Button>
                    )
                  )}

                  <Button
                    type="submit"
                    animate
                    isDisabled={formValues.prepaidCredits <= 0}
                  >
                    Videre
                  </Button>
                </div>
              </motion.div>
            )
          ) : (
            <motion.div
              key="confirmPurchase"
              className="flex h-full flex-col items-center gap-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SegmentHeader className="max-w-md" title="Bekræft tilkøb" />

              <div className="flex w-full max-w-[448px] flex-col items-center gap-y-6">
                <Card
                  className="flex w-full flex-col gap-y-6 p-6 text-sm"
                  theme="border"
                >
                  {formValues.creditType === "upgrade" ? (
                    <>
                      <div className="flex flex-col gap-y-3">
                        {monthlySummary.items.map((item, index) => (
                          <div
                            key={index}
                            className={twMerge(
                              "flex justify-between gap-x-4",
                              item.removed
                                ? "text-theme-on-background-variant line-through"
                                : "",
                            )}
                          >
                            <div>{item.name}</div>

                            <div className="text-right whitespace-nowrap">
                              {formatNumber(item.price)} kr.
                            </div>
                          </div>
                        ))}
                      </div>

                      <Separator />

                      <div className="flex justify-between gap-x-4 text-base font-medium">
                        <div>Total</div>

                        <div className="text-right whitespace-nowrap">
                          {formatNumber(monthlySummary.total)} kr/md.
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-y-3">
                        <div className="flex justify-between gap-x-4">
                          <div>
                            {formatNumber(selectedPrepaidCreditOption.credits)}{" "}
                            credits
                          </div>

                          <div className="text-right whitespace-nowrap">
                            {formatMoney(selectedPrepaidCreditOption.price)}
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div className="flex justify-between gap-x-4 text-base font-medium">
                        <div>Total</div>

                        <div className="text-right whitespace-nowrap">
                          {formatMoney(selectedPrepaidCreditOption.price)}
                        </div>
                      </div>
                    </>
                  )}
                </Card>

                <Controller
                  control={control}
                  name="acceptTerms"
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      className="max-w-[448px] gap-4"
                      name={field.name}
                      isSelected={field.value}
                      onChange={field.onChange}
                      aria-label="Accepter vilkår"
                    >
                      <span className="text-theme-on-background-variant grow text-xs leading-relaxed">
                        Jeg accepterer{" "}
                        <a
                          className="text-theme-on-background hover:text-theme-active hover:underline"
                          href="https://lassox.com/terms-conditions"
                          target="_blank"
                          onClick={(e) => e.stopPropagation()}
                        >
                          betingelserne
                        </a>
                        {formValues.creditType === "upgrade" ? (
                          <>
                            , og at der tillægges et beløb på{" "}
                            {formatMoney(
                              selectedUpgradeOption.price -
                                creditsData.extraCreditsPerMonthPrice,
                            )}{" "}
                            til mit månedlige abonnement, og at differencen
                            faktureres pro rata for nuværende periode.
                          </>
                        ) : (
                          <>
                            , og at der trækkes et engangsbeløb på{" "}
                            {formatMoney(selectedPrepaidCreditOption.price)} for
                            køb af{" "}
                            {formatNumber(selectedPrepaidCreditOption.credits)}{" "}
                            credits.
                          </>
                        )}
                      </span>
                    </Checkbox>
                  )}
                />
              </div>

              <div className="mt-auto flex gap-6">
                <Button
                  intent="secondary"
                  animate
                  onPress={() => {
                    setValue("view", "selectCredits");
                    setValue("acceptTerms", false);
                  }}
                >
                  Tilbage
                </Button>

                <Button
                  type="submit"
                  animate
                  isDisabled={!formState.isValid}
                  isPending={formState.isSubmitting}
                >
                  Bekræft
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Form>
    </SettingsContainer>
  );
}
